import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTOTPVerify, setShowTOTPVerify] = useState(false)
  const [showTOTPEnroll, setShowTOTPEnroll] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)
    try {
      await login(email, password)
      
      // Always check for MFA factors after successful password authentication
      const { data: factors } = await supabase.auth.mfa.listFactors()
      
      console.log('[Login] Password authentication successful, checking TOTP factors:', factors)
      
      if (factors?.totp && factors.totp.length > 0) {
        // User has TOTP set up, ALWAYS require verification regardless of AAL
        console.log('[Login] TOTP factor found, requiring verification')
        setShowTOTPVerify(true)
      } else {
        // User needs to set up TOTP first
        console.log('[Login] No TOTP factor found, requiring enrollment')
        setShowTOTPEnroll(true)
      }
      
      // Never redirect directly - always require MFA verification/enrollment
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to login'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`flex h-screen w-full items-center justify-center ${getThemeClass('gradients.primary')}`}>
      {showTOTPVerify ? (
        <TOTPVerify
          redirectTo="/admin"
          onComplete={() => {
            console.log('[Login] TOTP verification complete, redirecting to admin dashboard')
            navigate('/admin', { replace: true })
          }}
        />
      ) : showTOTPEnroll ? (
        <TOTPEnrollment
          onComplete={async () => {
            console.log('[Login] TOTP enrollment complete, redirecting to admin dashboard')
            navigate('/admin', { replace: true })
          }}
        />
      ) : (
        <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
          <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Login</h1>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            {formError && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className={`w-full ${getThemeClass('components.button.primary')}`}
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
            <div className="mt-4 text-center">
              <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
                Forgot your password?
              </Link>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
